import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSet } from 'react-use';
import { useDebounce } from 'use-debounce';
import { usePeriod } from '../../hooks/usePeriod';
import { useRouteParams } from '../../hooks/useRouteParams';
import { selectGroupSelectionData } from '../../state/selectors';
import { TabSlug } from '../../types';

import { IncomeDetailsEmpty } from './IncomeDetails.empty';
import { IncomeDetailsError } from './IncomeDetails.error';
import { IncomeDetailsLoading } from './IncomeDetails.loading';
import { SortBy } from './IncomeDetails.meta';
import { useIncomeDetails } from './useIncomeDetails';
import { IncomeDetailsContent } from './components/IncomeDetailsContent/IncomeDetailsContent';

interface Props {
  title: string;
}

export const IncomeDetails = ({ title }: Props) => {
  const [searchTerm, setSearchTerm] = useState('');

  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const { accountId, groupId } = useRouteParams();

  const [sortBy, setSortBy] = useState<SortBy>({ column: 'name', sort: 'none' });

  const [, { has: isRowExpanded, toggle: toggleRowExpanded, reset }] = useSet<string>();

  const { selectedTabIndex, tabList } = useSelector(selectGroupSelectionData(TabSlug.Income));

  const period = usePeriod();

  const { isFetching, isError, refetch, data } = useIncomeDetails({
    period: period.value,
    startDate: period.dates[0],
    endDate: period.dates[1],
    accountId,
    groupId,
    sortBy,
    filter: debouncedSearchTerm,
  });

  // Reset the set when changing tabs or fetching a new period
  useEffect(() => {
    reset();
  }, [reset, selectedTabIndex, isFetching]);

  if (isFetching) {
    return <IncomeDetailsLoading title={title} />;
  }

  if (isError) {
    return <IncomeDetailsError title={title} onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  const { tableData } = data;

  if (!tabList) {
    return <IncomeDetailsEmpty title={title} />;
  }

  if (!tableData) {
    return null;
  }

  return (
    <IncomeDetailsContent
      isRowExpanded={isRowExpanded}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      setSortBy={setSortBy}
      sortBy={sortBy}
      tableData={tableData}
      title={title}
      toggleRowExpanded={toggleRowExpanded}
    />
  );
};
